import { useState } from "react";
import { mediaService } from "@/services/media.service";
import { membersService } from "@/services/members.service";

/**
 * Extracts the S3 key from a full S3 URL.
 * Example: https://bucket.s3.amazonaws.com/member-avatars/abc.jpg?AWSAccessKeyId=... -> member-avatars/abc.jpg
 */
export function extractS3Key(url?: string): string | undefined {
  if (!url) return undefined;
  // Handles keys with or without folders
  const match = url.match(/\.amazonaws\.com\/([^?]+)/);
  return match ? match[1] : undefined;
}

/**
 * Hook for member avatar deletion logic (S3/media + member update).
 * Handles API, key extraction, state update, and error.
 */
export function useDeleteMemberAvatar() {
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * Deletes the avatar for a member (removes from S3 and member profile).
   * @param memberId - Member's ID
   * @param avatarUrl - Current avatar URL (full S3 URL)
   * @returns success boolean
   */
  const deleteAvatar = async (memberId: string, avatarUrl?: string) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      if (avatarUrl) {
        const key = extractS3Key(avatarUrl);
        console.log(`Deleting avatar: ${key}`);
        if (key) await mediaService.deleteMedia(key);
      }
      await membersService.updateMember(memberId, { avatar: null });
      // Update localStorage: if selectedMember exists, update its avatar; else update member
      const selectedMemberStr = localStorage.getItem("selectedMember");
      if (selectedMemberStr) {
        const selectedMember = JSON.parse(selectedMemberStr);
        if (selectedMember && selectedMember.id === memberId) {
          selectedMember.avatar = null;
          localStorage.setItem(
            "selectedMember",
            JSON.stringify(selectedMember)
          );
        }
      } else {
        const memberStr = localStorage.getItem("member");
        if (memberStr) {
          const member = JSON.parse(memberStr);
          if (member && member.id === memberId) {
            member.avatar = null;
            localStorage.setItem("member", JSON.stringify(member));
          }
        }
      }
      setDeleting(false);
      window.location.reload();
      return true;
    } catch (err: any) {
      setDeleteError(err?.message || "Failed to delete avatar");
      setDeleting(false);
      return false;
    }
  };

  return { deleteAvatar, deleting, deleteError };
}
